import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * 「今天星球上发生的事」的上拉面板外壳（UI-WORLD-LIVING-117-1，用户 10-15 经 I：「先逛逛」落到全屏星球，列表退进上拉面板）。
 * - 贴在星球底边，收着时只露把手：标题和一个向上的小箭头、此刻一句、最上面那件事；点一下（或回车、空格）拉起，再点收起；
 *   在把手上往上拉一下也拉起、往下拉一下收起（评审 R1 第 4 条：把手画成能拉的样子，就得能拉）；
 * - 不是模态：拉起来最多到半屏左右，星球上半截和居民灯照样看得见、能点（R1 第 1 条）；Esc 收起，焦点回到把手（面板里开着大图时 Esc 先关大图）；
 * - 星球上点开了一处的短卡、选城卡（yielded）：面板让开、收到把手，不盖住那张卡；卡关了面板回来，接着刚才读到的地方（R2 第 1 条）。
 *   让开时点把手（指针、键盘都一样）就是拉起：顺手把那张卡关上（onReclaim），按下那一刻是收着的就拉起，不被「点外面关卡」那一下反过来（R3 第 1 条）；
 * - 每次进「先逛逛」都是收着落地（R2 第 5 条）；只有从面板里点进居民主页、再回来，才接着看：开没开、滚到哪记在 sessionStorage，
 *   点进去那一下记一个「回来接着看」的记号，回来挂上时用掉。页面再告诉面板这一次是不是「回来」（returnable：浏览器后退、或带着居民主页「返回」的标记）；
 *   点进主页以后改走欢迎页再「跳过」进来的，记号在也不接着看（117-2，补 117-1 记下的小洞）。
 * - 标题跟着这一层走（title）：星球层「今天星球上发生的事」，城市层「今天 X 发生的事」（117-2，I 10-15）；换了一层（layer 变了：星球和城市之间、换一座城）
 *   说的就是另一处的今天了，从头看，开没开照旧。回来时挂上那一刻就是那一层，不算换。记号用掉以后「这次是回来的」照旧交给面板里的内容（DailyReturn），
 *   名单读得慢、内容晚挂上时（冷缓存）也照样恢复「再看看」「例行」的展开，内容齐了再滚回原处（R3 挑刺）；
 * - 内容（GuestWorldStories）一直挂着、只是收起时藏起来（hidden）：读屏、键盘都跳过收着的内容；拉起时内容带进场标记（data-motion="enter"）；
 * - 标题是 h2（面板里的卡片是 h3），把手按钮在标题里；
 * - 人数按钮（点开居民名单）在把手旁边，收着也看得见：落地一步就能认识所有居民，不用先拉起面板。
 */
const SHEET_STORE = "petsoul:public-world:guest-daily-sheet:v1";
const RETURN_STORE = "petsoul:public-world:guest-daily-return:v1";
/** 在把手上拉多远算「拉」（不到这个距离就当点了一下） */
const DRAG_PX = 24;
function readReturn(): boolean {
  try { return sessionStorage.getItem(RETURN_STORE) === "1"; } catch { return false; }
}
function readSheet(returnable: boolean): { returning: boolean; open: boolean; scroll: number } {
  if (!returnable || !readReturn()) return { returning: false, open: false, scroll: 0 };
  try {
    const value = JSON.parse(sessionStorage.getItem(SHEET_STORE) || "null") as { open?: unknown; scroll?: unknown } | null;
    return { returning: true, open: value?.open === true, scroll: typeof value?.scroll === "number" && Number.isFinite(value.scroll) && value.scroll >= 0 ? value.scroll : 0 };
  } catch { return { returning: true, open: false, scroll: 0 }; }
}

/** 这次挂上是不是从面板点进居民主页以后回来的；内容齐了叫 restore()，面板滚回刚才读到的地方（只滚一次） */
const DailyReturn = createContext<{ returning: boolean; restore: () => void }>({ returning: false, restore: () => {} });
export function useDailyReturn() { return useContext(DailyReturn); }

export const DAILY_TITLE = "今天星球上发生的事";

export function DailySheet({ summary, peek, count, yielded = false, onReclaim, title = DAILY_TITLE, layer = "planet", returnable = true, children }: {
  summary: string | null; peek: string | null; count?: ReactNode;
  /** 面板标题：星球层用 DAILY_TITLE，城市层说这座城 */
  title?: string;
  /** 这是哪一层（地图给的标识）；变了就从头看 */
  layer?: string;
  /** 这一次挂上算不算「回来」（浏览器后退、或从居民主页「返回」带着标记）；不算的话记号在也收着落地 */
  returnable?: boolean;
  /** 星球上开着短卡、选城卡：面板让开 */
  yielded?: boolean;
  /** 让开时把手被按下：请地图把那张卡关上 */
  onReclaim?: () => void;
  children: ReactNode;
}) {
  const [stored] = useState(() => readSheet(returnable));
  const [open, setOpen] = useState(stored.open);
  // 拉起、收起过（或回来时本来就开着）：落地那一下的入场淡入不再给它，免得关上时「.is-open」一去掉，入场淡入从头重放、整块面板消失 0.9 秒（评审 R4 高 1）
  const [touched, setTouched] = useState(stored.open);
  // 让开的时候看起来收着；拉起、收起照这个算
  const shown = open && !yielded;
  const bodyRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const scrolled = useRef(stored.scroll);
  // 回来时要滚到的地方：内容齐了（restore）才滚，滚过就不再管
  const target = useRef<number | null>(stored.open ? stored.scroll : null);
  const drag = useRef<{ id: number; y: number; shown: boolean; moved: boolean } | null>(null);
  const bodyId = useId();
  const save = (next: { open: boolean; scroll: number }) => { scrolled.current = next.scroll; try { sessionStorage.setItem(SHEET_STORE, JSON.stringify(next)); } catch { /* presentation only */ } };
  useEffect(() => {
    // 回来接着看的记号用掉；不是回来的（新进来）把上次记的开合清掉，落地一律收着
    try { sessionStorage.removeItem(RETURN_STORE); } catch { /* presentation only */ }
    if (!stored.open) save({ open: false, scroll: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    // 拉起来（挂上时、或让开以后回来）滚回刚才读到的地方；藏起来的时候浏览器不留滚动位置
    const body = bodyRef.current;
    if (!shown || !body) return;
    body.scrollTop = target.current ?? scrolled.current;
    // 内容已经够长、滚到了：不用再等 restore
    if (target.current !== null && Math.abs(body.scrollTop - target.current) < 1) target.current = null;
  }, [shown]);
  const restore = useCallback(() => {
    const body = bodyRef.current;
    if (target.current === null || !body || body.hidden) return;
    body.scrollTop = target.current; target.current = null;
  }, []);
  const back = useMemo(() => ({ returning: stored.returning, restore }), [stored.returning, restore]);
  const layerRef = useRef(layer);
  useLayoutEffect(() => {
    if (layerRef.current === layer) return;
    layerRef.current = layer;
    // 换了一层：不再等着滚回上次读到的地方，从头看
    target.current = null;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    save({ open, scroll: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer]);
  const setSheet = (next: boolean) => {
    if (next && yielded) onReclaim?.();
    setTouched(true); setOpen(next); save({ open: next, scroll: next ? scrolled.current : 0 });
  };
  return <section className={`ps-daily-sheet${shown ? " is-open" : ""}`} aria-label={title} data-testid="daily-sheet" data-touched={touched ? "" : undefined} data-map-interactive onKeyDown={event => {
    if (!shown || event.key !== "Escape" || document.querySelector("dialog[open]")) return;
    event.preventDefault(); event.stopPropagation(); setSheet(false); handleRef.current?.focus();
  }}>
    <div className="ps-daily-sheet__head">
      <h2 className="ps-daily-sheet__heading">
        <button ref={handleRef} type="button" className="ps-daily-sheet__handle" aria-expanded={shown} aria-controls={bodyId}
          onPointerDown={event => {
            // 记下按下那一刻是开着还是收着（让开时按下去，地图会因为「点外面」先把卡关掉，面板跟着回来；那一下点击要算拉起）
            drag.current = { id: event.pointerId, y: event.clientY, shown, moved: false };
            try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* not a live pointer */ }
          }}
          onPointerUp={event => {
            const start = drag.current;
            if (!start || start.id !== event.pointerId || Math.abs(event.clientY - start.y) < DRAG_PX) return;
            // 拉过了：往上拉开、往下收起（已经是那样就不动）；接下来那一下 click 不再切换
            const up = event.clientY < start.y;
            start.moved = true;
            if (up !== start.shown) setSheet(up);
          }}
          onPointerCancel={() => { drag.current = null; }}
          onClick={() => {
            const start = drag.current; drag.current = null;
            if (start?.moved) return;
            setSheet(!(start ? start.shown : shown));
          }}>
          <span className="ps-daily-sheet__grip" aria-hidden="true" />
          <span className="ps-daily-sheet__title">{title}<span className={`ps-daily-sheet__chev${shown ? " is-open" : ""}`} aria-hidden="true" /></span>
          {summary || (!shown && peek) ? <span className="ps-daily-sheet__peek">{summary ? <span className="ps-daily-sheet__summary">{summary}</span> : null}{!shown && peek ? <span className="ps-daily-sheet__lead">{peek}</span> : null}</span> : null}
          <span className="visually-hidden">{shown ? "，收起" : "，拉起来看看"}</span>
        </button>
      </h2>
      {count ? <div className="ps-daily-sheet__count">{count}</div> : null}
    </div>
    <div ref={bodyRef} id={bodyId} className="ps-daily-sheet__body" hidden={!shown} data-motion={shown ? "enter" : undefined}
      onScroll={event => save({ open: true, scroll: event.currentTarget.scrollTop })}
      onClickCapture={event => {
        // 从面板里点进居民主页：记下「回来接着看」，回来时照旧开着、滚到这里
        if (event.target instanceof Element && event.target.closest('a[href^="/world/residents/"]')) {
          save({ open: true, scroll: event.currentTarget.scrollTop });
          try { sessionStorage.setItem(RETURN_STORE, "1"); } catch { /* presentation only */ }
        }
      }}>
      <DailyReturn.Provider value={back}>{children}</DailyReturn.Provider>
    </div>
  </section>;
}
